import type { components } from '@joyful/openapi/types.gen'
import {
  AccountSelectionCommand,
  AccountSelectionCommandGroupedOptions,
  AccountSelectionCommandInput,
  AccountSelectionCommandList,
} from '../navbar/AccountSelectionCommand'
import { AuthSplashSignOutButton } from './AuthSplashSignOutButton'
import { CurrentUserContext } from '../../utils/currentUserContext'
import {
  Box,
  EmptyState,
  Heading,
  Splash,
  Stack,
  SpinningLoader,
} from '../../../core'

type CurrentUserAccountRead = components['schemas']['CurrentUserAccountRead']

export interface SelectAccountSplashProps {
  isPending?: boolean
  onSelectAccount: (account: CurrentUserAccountRead) => void
}

export function SelectAccountSplash({
  isPending = false,
  onSelectAccount,
}: SelectAccountSplashProps) {
  const currentUser = CurrentUserContext.useRequiredContext()

  if (currentUser.accounts.length === 0) {
    return (
      <Splash position="relative" variant="plain">
        <AuthSplashSignOutButton />
        <EmptyState
          subtitle={`Your user account is not associated with a practice account yet. Please contact your administrator to complete the setup.`}
        >
          {`Account Setup Required`}
        </EmptyState>
      </Splash>
    )
  }

  return (
    <Splash placement="start" position="relative">
      <AuthSplashSignOutButton />
      <Box maxWidth="prose" width="full">
        <Stack gap="lg" width="full">
          <header className="text-center">
            <Heading level={2} margins="none">
              {`Select a practice account`}
            </Heading>
            <p className="text-tremor-content">
              {`Please select which practice account you want to view.`}
            </p>
          </header>

          {isPending ? (
            <SpinningLoader size="lg" />
          ) : (
            <div className="overflow-hidden rounded-md border">
              <AccountSelectionCommand
                actions={{
                  onSelect: (account) => {
                    if (account !== null) {
                      const selectedAccount =
                        currentUser.accounts.find((currentUserAccount) => {
                          return currentUserAccount.id === account.id
                        }) ?? null

                      if (selectedAccount !== null) {
                        onSelectAccount(selectedAccount)
                      }
                    }
                  },
                }}
                state={{
                  accounts: currentUser.accounts,
                  activeAccount: null,
                  isDisabled: false,
                  size: 'lg',
                }}
              >
                <AccountSelectionCommandInput
                  placeholder="Switch to account…"
                  autoFocus
                />

                <AccountSelectionCommandList maxHeight="2xl">
                  {currentUser.accounts.length > 0 ? (
                    <AccountSelectionCommandGroupedOptions />
                  ) : null}
                </AccountSelectionCommandList>
              </AccountSelectionCommand>
            </div>
          )}
        </Stack>
      </Box>
    </Splash>
  )
}
